import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React from 'react';
import { helpingMethod } from '../../utils/HelpingMethod';

dayjs.extend(customParseFormat);

interface FormDatePickerProps {
  value: string | null;
  onChange: (_value: string | null) => void;
  sx?: object;
  minDate?: Dayjs | undefined;
  maxDate?: Dayjs | undefined;
  [key: string]: any;
}

const FormDatePicker: React.FC<FormDatePickerProps> = React.memo(function FormDatePicker({
  value,
  onChange,
  sx = {},
  minDate,
  maxDate,
  label: _label,
  placeholder: _placeholder,
  ...props
}) {
  const theme = useTheme();

  const convertedValue = React.useMemo(() => {
    if (!value) return null;
    const parsed = dayjs(value, 'DD/MM/YYYY', true);
    return parsed.isValid() ? parsed : dayjs(value);
  }, [value]);

  const handleChange = React.useCallback(
    (newValue: Dayjs | null) => {
      if (newValue && newValue.isValid()) {
        onChange(helpingMethod.formatDate(newValue.toDate()));
      } else {
        onChange(null);
      }
    },
    [onChange],
  );

  // Track the container width so the popper can match it
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    // Initialize
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setContainerWidth(Math.round(cr.width));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      <MUIDatePicker
        value={convertedValue}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        format="DD/MM/YYYY"
        sx={{
          width: '100%',
          ...(sx as any),
        }}
        slotProps={{
          textField: {
            sx: {
              width: '100%',
              '& .MuiPickersInputBase-root': {
                borderRadius: `${theme.borderRadius.md}px`,
                fontSize: '14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: `${theme.borderRadius.sm}px`,
              },
              '& .MuiPickersSectionList-root ': {
                height: '28px',
                padding: '12px 0px',
                display: 'flex',
                alignItems: 'center',
              },
            },
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                borderRadius: `${theme.borderRadius.sm}px`,
                boxShadow: theme.customShadows.dropdown,
                border: `1px solid ${theme.palette.datePicker.border}`,
                padding: '16px',
                // Match the calendar width to the input/container width when available
                width: containerWidth ? `${containerWidth}px - 30px` : undefined,
              },
              '& .MuiPickersCalendarHeader-root': {
                marginBottom: '16px',
              },
              '& .MuiPickersCalendarHeader-label': {
                fontWeight: 600,
                fontSize: '16px',
                color: theme.palette.datePicker.headerBlue,
              },
              '& .MuiPickersCalendarHeader-switchViewButton': {
                color: theme.palette.datePicker.headerBlue,
                transform: 'rotate(270deg)',
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: theme.palette.datePicker.headerBlue,
              },
              '& .MuiDayCalendar-weekDayLabel': {
                fontWeight: 500,
                fontSize: '12px',
                color: theme.palette.datePicker.weekdayGray,
                textTransform: 'uppercase',
              },
              '& .MuiPickersDay-root': {
                fontWeight: 400,
                fontSize: '14px',
                color: theme.palette.datePicker.dayText,
                width: 36,
                height: 36,
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: theme.palette.datePicker.hoverBg,
                },
              },
              '& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus':
                {
                  backgroundColor: theme.palette.datePicker.selectedBg,
                  color: theme.palette.datePicker.selectedText,
                  fontWeight: 600,
                },
              '& .MuiPickersDay-root.MuiPickersDay-today': {
                color: theme.palette.datePicker.todayText,
                fontWeight: 600,
                border: 'none',
                backgroundColor: 'transparent',
              },
              '& .MuiPickersDay-root.MuiPickersDay-today.Mui-selected': {
                backgroundColor: theme.palette.datePicker.selectedBg,
                color: theme.palette.datePicker.selectedText,
              },
            },
          },
        }}
        {...props}
      />
    </Box>
  );
});

export default FormDatePicker;
